import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Plus, Pencil, Star, MoreVertical,
  Search, Users, X, UserPlus, Coins, Trash2, Download,
} from "lucide-react";

/**
 * Sidebar of the ErosCoach page — sessions list, search, favorites,
 * per-session actions menu. Extracted from `pages/ErosCoach.jsx` as part
 * of the P1 #2 refactor (pure code-move, zero behaviour change).
 *
 * All state lives in the parent `ErosCoach`. Handlers are passed as props.
 */
export default function CoachSidebar({
  isMobile = false,
  // Display state
  sessionsLoading,
  filteredSessions,
  currentSessionId,
  isSearchActive,
  searchResults,
  savedSearchQuery,
  showFavoritesOnly,
  coinBalance,
  isAdmin,
  // Setters / handlers
  navigate,
  setMobileSidebarOpen,
  setSearchModalOpen,
  setShowCoinPurchaseModal,
  setShowFavoritesOnly,
  fullyClearSearch,
  loadSession,
  createNewSession,
  openNewSessionModal,
  fetchSessions,
  toggleFavorite,
  openEditTitle,
  openAssignModal,
  confirmDelete,
  exportSession,
}) {
  return (
    <div className="flex flex-col h-full">
      {/* Mobile-only: New session action */}
      {isMobile && (
        <div className="p-3 pr-12 border-b border-divine-gold/20">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (openNewSessionModal) {
                openNewSessionModal();
              } else {
                createNewSession();
                setMobileSidebarOpen(false);
              }
            }}
            className="w-full justify-center gap-2 text-divine-gold hover:text-white hover:bg-divine-gold/20 border border-divine-gold/30"
            data-testid="mobile-new-session-btn"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">New</span>
          </Button>
        </div>
      )}

      {/* Top Actions - Fixed */}
      <div className="p-3 border-b border-divine-gold/20">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSearchModalOpen(true)}
            className="flex-1 justify-start gap-2 text-white/60 hover:text-white hover:bg-white/10"
            data-testid="search-sessions-btn"
          >
            <Search className="w-4 h-4" />
            <span className="text-xs">Search All</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/relationships")}
            className="flex-1 justify-start gap-2 text-white/60 hover:text-white hover:bg-white/10"
            data-testid="relationships-link"
          >
            <Users className="w-4 h-4" />
            <span className="text-xs">Relationships</span>
          </Button>
        </div>

        {/* Mobile-only: Coin Balance Display */}
        {isMobile && (
          <div
            onClick={() => {
              // URL is now authoritative (kept in sync by ErosCoach's mirror
              // effects), so window.location has the current match + session.
              const from = `${window.location.pathname}${window.location.search}${window.location.hash}`;
              navigate("/plans?tab=topup", { state: { from } });
              setMobileSidebarOpen(false);
            }}
            className="mt-2 flex items-center justify-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg cursor-pointer hover:bg-amber-500/20 transition-colors"
            data-testid="mobile-coin-balance"
          >
            <Coins className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 font-medium text-sm">
              {coinBalance !== null ? coinBalance.toLocaleString() : "—"} coins
            </span>
          </div>
        )}
      </div>

      {/* Sessions Header - Fixed. On desktop the label is a clickable
          "+ New Session" button that opens the Relationship picker modal;
          the counter stays as a plain span next to it. On mobile the label
          is non-interactive because the [New] action lives above. */}
      <div className="flex items-center justify-between p-3 border-b border-divine-gold/20">
        {!isMobile && openNewSessionModal ? (
          <button
            type="button"
            onClick={openNewSessionModal}
            className="flex items-center gap-1 text-divine-gold hover:text-white text-sm font-medium transition-colors"
            data-testid="desktop-new-session-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>New Session</span>
          </button>
        ) : (
          <span className="text-white/60 text-sm font-medium">Sessions</span>
        )}
        <span className="text-white/40 text-xs">{filteredSessions.length}</span>
      </div>

      {/* Favorites Filter */}
      <div className="px-3 py-2 border-b border-divine-gold/10">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
          className={`w-full justify-start gap-2 text-sm ${
            showFavoritesOnly
              ? "text-yellow-400 bg-yellow-400/10"
              : "text-white/60 hover:text-white"
          }`}
          data-testid="favorites-filter-btn"
        >
          <Star className={`w-4 h-4 ${showFavoritesOnly ? "fill-yellow-400" : ""}`} />
          <span>Favorite sessions</span>
        </Button>
      </div>

      {/* Search Active Indicator */}
      {isSearchActive && (
        <div className="px-3 py-2 bg-divine-gold/10 border-b border-divine-gold/20 flex items-center justify-between">
          <span className="text-divine-gold text-xs font-medium">
            {searchResults.length} Results for &quot;{savedSearchQuery}&quot;
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 text-divine-gold hover:text-white"
            onClick={fullyClearSearch}
            title="Clear search"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      )}

      {/* Sessions List - Scrollable */}
      <div className="flex-1 min-h-0 overflow-y-auto p-2">
        {sessionsLoading ? (
          <div className="text-white/40 text-sm text-center py-4">Loading...</div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-white/40 text-sm text-center py-4">
            {showFavoritesOnly ? "No favorite sessions" : "No sessions yet"}
          </div>
        ) : (
          <div className="space-y-1">
            {filteredSessions.map(session => (
              <div
                key={session.session_id}
                className={`group p-2 rounded-lg cursor-pointer transition-all ${
                  currentSessionId === session.session_id
                    ? "bg-divine-gold/20 border border-divine-gold/40"
                    : "bg-white/5 hover:bg-white/10 border border-transparent"
                }`}
                onClick={() => {
                  loadSession(session.session_id);
                  if (isMobile) setMobileSidebarOpen(false);
                }}
                data-testid={`session-item-${session.session_id}`}
              >
                {/* Session Title */}
                <p className="text-white text-sm font-medium truncate pr-6">
                  {session.is_favourite && <Star className="w-3 h-3 inline mr-1 text-yellow-400 fill-yellow-400" />}
                  {session.title}
                </p>

                {/* Message count and menu */}
                <div className="flex items-center justify-between mt-1">
                  <span className="text-white/40 text-xs">
                    {session.message_count} message{session.message_count !== 1 ? "s" : ""}
                    {isSearchActive && session.search_matches && (
                      <span className="ml-1 text-divine-gold">
                        ({session.search_matches} match{session.search_matches !== 1 ? "es" : ""})
                      </span>
                    )}
                  </span>

                  {/* Mini Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`h-6 w-6 text-white/40 hover:text-white ${
                          isMobile ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                        }`}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`session-menu-${session.session_id}`}
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="bg-divine-charcoal border-divine-gold/30 min-w-[180px]"
                    >
                      <DropdownMenuItem
                        onClick={(e) => toggleFavorite(session, e)}
                        className="text-white hover:bg-white/10 cursor-pointer"
                      >
                        <Star className={`w-4 h-4 mr-2 ${session.is_favourite ? "text-yellow-400 fill-yellow-400" : ""}`} />
                        {session.is_favourite ? "Remove Favorite" : "Favorite"}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => openEditTitle(session, e)}
                        className="text-white hover:bg-white/10 cursor-pointer"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => openAssignModal(session, e)}
                        className="text-white hover:bg-white/10 cursor-pointer"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add to relationship
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem
                        onClick={(e) => confirmDelete(session.session_id, e)}
                        className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                      {isAdmin && (
                        <>
                          <DropdownMenuSeparator className="bg-white/10" />
                          <DropdownMenuItem
                            onClick={(e) => exportSession(session, e)}
                            className="text-orange-400 hover:bg-orange-500/10 cursor-pointer"
                            data-testid={`session-export-${session.session_id}`}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Export
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
